import { AdminType, APIDelete, APIGet, APIPost, APIPut, APIUrl } from "api/protocols";
import { CancelToken } from "axios";
import { IUserPermissions } from "redux/reducers/models/user";

type TPutFacilityAdmin = {
  id: number;
  first_name?: string;
  last_name?: string;
  email?: string;
  username?: string;
  role_id?: number;
  quick_code?: number;
  roles?: Array<{ id: number; wage?: string | number }>;
};

type TPutFacilityAdminPermissions = {
  user_id: number;
  accounts_charge_all: boolean;
  accounts_post_adjustment: boolean;
  admin_accounts_disable: boolean;
  admin_permissions_edit: boolean;
  admin_accounts_edit: boolean;
  products_archive: boolean;
  products_view_all: boolean;
  products_create: boolean;
  products_edit: boolean;
  products_edit_cost: boolean;
  products_edit_price: boolean;
  orders_remove_line_item: boolean;
  orders_remove_line_item_after_send: boolean;
  orders_edit_line_item_price: boolean;
  orders_apply_discount: boolean;
  orders_void: boolean;
  orders_refund_tip: boolean;
  orders_edit_tip_allocations: boolean;
  orders_refund_service_fee: boolean;
  reports_access: boolean;
  tee_sheet_credit_card_override: boolean;
  tee_sheet_charge_no_show: boolean;
  orders_edit_user: boolean;
  orders_edit_register: boolean;
  orders_backdate: boolean;
  table_service_table_override: boolean;
  reservations_credit_card_override: boolean;
  merge_customers: boolean;

  cash_outs_create: boolean;
  cash_outs_edit: boolean;
  cash_outs_view_summary: boolean;

  memberships_apply_manual: boolean;
  user_edit_customer_type: boolean;
  discounts_create: boolean;
  gift_cards_view: boolean;
  customers_edit_note: boolean;
  bookings_edit_note: boolean;
  gift_cards_import: boolean;
  gift_cards_enable: boolean;

  courses_create: boolean;
  divisions_create: boolean;
  booking_restrictions_edit: boolean;
  tickets_unredeem: boolean;
};

interface IPasswordResetCode {
  email: string;
  facility_id?: number;
}

export interface IFacilityAdmin {
  id: number;
  client_id: number;
  facility_id: number;
  email: string;
  first_name: string;
  last_name: string;
  full_name: string;
  username: string;
  state: string;
  user_type: number;
  profile_image_source: string;
  phone_number: string;
  role_id: number;
  has_quick_code?: boolean;
  permissions: IUserPermissions;
  roles?: Array<{id: number; title: string; wage?: string;}>;
}

export async function GetFacilityAdmin(params: any, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IFacilityAdmin[]>(
    APIUrl(AdminType.FacilityAdmin, "/client/admin"),
    params,
    useGlobalLoader,
    token,
  );
}

export async function PostFacilityAdmin(params: any, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/client/admin"), params, useGlobalLoader);
}

export async function PutFacilityAdmin(params: TPutFacilityAdmin, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/admin"), params, useGlobalLoader);
}

export async function PutFacilityAdminPermissions(params: TPutFacilityAdminPermissions, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/admin/permission"), params, useGlobalLoader);
}

export async function PutDisableFacilityAdmin(params: { admin_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/admin/disable"), params, useGlobalLoader);
}

export async function PutReactivateFacilityAdmin(params: { admin_id: number }, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/admin/reactivate"), params, useGlobalLoader);
}

export async function PostPasswordResetCode(params: IPasswordResetCode, useGlobalLoader: boolean) {
  return await APIPost(APIUrl(AdminType.FacilityAdmin, "/client/admin/reset"), params, useGlobalLoader);
}

export interface IPermissionDetailsList {
  [categoryKey: string]: {
    [permissionKey: string]: Array<IPermissionDetails>;
  };
}

// export interface IPermissionDetailsList {
//   [key: string]: IPermissionDetails[];
// }

export interface IPermissionDetails {
  id: number;
  title: string;
  category: string;
  subcategory: string;
  key: string;
  description: string;
  client_only: boolean;
  enabled?: boolean;
}

export async function GetFacilityPermissions(params: any, useGlobalLoader: boolean) {
  return await APIGet<IPermissionDetailsList>(
    APIUrl(AdminType.FacilityAdmin, "/client/admin/permission/detail"),
    params,
    useGlobalLoader,
  );
}

export interface IFacilityRole {
  id?: number;
  title?: string;
  description?: string;
  wage?: string;
  default?: boolean;
  locked?: boolean;
  admins_count?: number;
  permissions?: IPermissionWithPivot[];
}

export interface INewFacilityRole {
  id?: number;
  title?: string;
  description?: string;
  default?: boolean;
  locked?: boolean;
  permissions?: IPermissionPivot[];
}

export interface IPermissionWithPivot extends IPermissionDetails {
  pivot?: IPermissionPivot;
}

export interface IPermissionPivot {
  permission_id?: number;
  role_id?: number;
  boolean_value?: boolean;
}
interface IFacilityRoleParams {
  id?: number;
  permissions?: boolean;
  admins?: boolean;
}

export async function GetFacilityRoles(params: IFacilityRoleParams, useGlobalLoader: boolean, token?: CancelToken) {
  return await APIGet<IFacilityRole[]>(
    APIUrl(AdminType.FacilityAdmin, "/client/admin/role"),
    params,
    useGlobalLoader,
    token,
  );
}

export interface IRolePermission {
  id?: number;
  enabled?: boolean;
}

export interface IPostFacilityRoleParams {
  title?: string;
  description?: string;
  locked?: boolean;
  default?: boolean;
  wage?: string;
  permissions?: IPostPermissionPivot[];
}

export interface IPostPermissionPivot {
  permission_id: number;
  value_type: string;
  value: boolean;
}

export async function PostFacilityRole(params: IPostFacilityRoleParams, useGlobalLoader: boolean) {
  return await APIPost<IFacilityRole>(APIUrl(AdminType.FacilityAdmin, "/client/admin/role"), params, useGlobalLoader);
}

export interface IDeleteFacilityRoleParams {
  id: number;
}

export async function DeleteFacilityRole(params: IDeleteFacilityRoleParams, useGlobalLoader: boolean) {
  return await APIDelete(APIUrl(AdminType.FacilityAdmin, "/client/admin/role"), params, useGlobalLoader);
}

export interface IPutFacilityRoleParams {
  id: number;
  title?: string;
  description?: string;
  permissions?: any;
  wage?: string | null;
  apply_to_existing_users?: boolean;
}

export async function PutFacilityRole(params: IPutFacilityRoleParams, useGlobalLoader: boolean) {
  return await APIPut(APIUrl(AdminType.FacilityAdmin, "/client/admin/role"), params, useGlobalLoader);
}
